"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button, Callout, Flex, Heading, Text, TextField, Box } from "@radix-ui/themes";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import ApiError from "@/api/ApiError";
import { LoginFormValues, useLoginMutation } from "@/views/home/hooks/use-login-mutation";
import { readPendingVerification } from "@/views/sign-up/pending-verification";
import S from "./index.module.scss";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getLoginErrorMessageKey = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401) {
    return "errors.invalidCredentials";
  }

  return error ? "errors.common" : null;
};

const AuthorizationForm = () => {
  const t = useTranslations("signInPage.form");
  const [hasPendingVerification, setHasPendingVerification] = useState(false);

  useEffect(() => {
    setHasPendingVerification(readPendingVerification() !== null);
  }, []);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const loginMutation = useLoginMutation();
  const errorMessageKey = getLoginErrorMessageKey(loginMutation.error);

  return (
    <Box className={S.card}>
      <form className={S.form} onSubmit={handleSubmit(values => loginMutation.mutate(values))}>
        <Flex direction="column" gap="2">
          <Heading size="5">{t("title")}</Heading>
          <Text size="2" color="gray">
            {t("description")}
          </Text>
          {hasPendingVerification ? <Link href="/sign-up">{t("continueVerification")}</Link> : null}
        </Flex>

        {errorMessageKey ? (
          <Callout.Root color="red" role="alert">
            <Callout.Text>{t(errorMessageKey)}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex direction="column" gap="4">
          <label className={S.field}>
            <Text as="span" size="2" weight="medium">
              {t("fields.email.label")}
            </Text>
            <TextField.Root
              type="email"
              placeholder={t("fields.email.placeholder")}
              autoComplete="email"
              color={errors.email ? "red" : undefined}
              {...register("email", {
                required: t("validation.email.required"),
                setValueAs: value => value.trim(),
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t("validation.email.invalid"),
                },
              })}
            />
            {errors.email ? (
              <Text size="1" color="red">
                {errors.email.message}
              </Text>
            ) : null}
          </label>

          <label className={S.field}>
            <Text as="span" size="2" weight="medium">
              {t("fields.password.label")}
            </Text>
            <TextField.Root
              type="password"
              placeholder={t("fields.password.placeholder")}
              autoComplete="current-password"
              color={errors.password ? "red" : undefined}
              {...register("password", {
                required: t("validation.password.required"),
                validate: value => {
                  if (!value.trim()) {
                    return t("validation.password.required");
                  }

                  if (value.length < 8) {
                    return t("validation.password.minLength");
                  }

                  return true;
                },
              })}
            />
            {errors.password ? (
              <Text size="1" color="red">
                {errors.password.message}
              </Text>
            ) : null}
          </label>
        </Flex>

        <Button
          color="violet"
          type="submit"
          size="3"
          loading={loginMutation.isPending}
          disabled={loginMutation.isPending}
          className={S.submitButton}
        >
          {t("submit")}
        </Button>
      </form>
    </Box>
  );
};

export default AuthorizationForm;
